 import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person,
  Settings,
  Notifications,
  Security,
  TrendingUp,
  Groups,
  Analytics
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';
import { isStoreAdmin } from '../../utils/roles';

const Dashboard = () => {
  const { state } = useApp();
  const navigate = useNavigate();

  // Redirect store admin and admin users to the admin dashboard
  useEffect(() => {
    if (isStoreAdmin(state.user?.role) || state.user?.role === 'admin') {
      navigate('/admin/dashboard', { replace: true });
    }
  }, [state.user?.role, navigate]);

  const stats = [
    {
      title: 'Profile Status',
      value: 'Active',
      icon: <Person sx={{ fontSize: 40 }} />,
      color: 'success.main'
    },
    {
      title: 'Account Type',
      value: state.user?.role?.toUpperCase() || 'USER',
      icon: <Security sx={{ fontSize: 40 }} />,
      color: 'primary.main'
    },
    {
      title: 'Member Since',
      value: state.user?.createdAt 
        ? new Date(state.user.createdAt).getFullYear()
        : new Date().getFullYear(),
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: 'info.main'
    }
  ];

  const quickActions = [
    {
      title: 'View Profile',
      description: 'View and manage your profile information',
      icon: <Person />,
      link: '/profile',
      color: 'primary'
    },
    {
      title: 'Settings',
      description: 'Configure your account preferences',
      icon: <Settings />,
      link: '/settings',
      color: 'secondary',
      disabled: true
    },
    {
      title: 'Security',
      description: 'Manage your account security settings',
      icon: <Security />,
      link: '/security',
      color: 'error',
      disabled: true
    }
  ];

  const recentActivity = [
    {
      title: 'Account Created',
      description: `Welcome to ${appConfig.name}!`,
      time: state.user?.createdAt 
        ? new Date(state.user.createdAt).toLocaleDateString()
        : 'Today'
    },
    {
      title: 'Profile Setup',
      description: 'Your profile has been set up successfully',
      time: state.user?.createdAt 
        ? new Date(state.user.createdAt).toLocaleDateString()
        : 'Today'
    }
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          Dashboard
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Welcome back, {state.user?.f_name || state.user?.name || state.user?.mobile || 'User'}!
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ color: stat.color, mr: 2 }}>
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h6" component="div">
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Quick Actions */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card 
                    variant="outlined"
                    sx={{ 
                      height: '100%',
                      opacity: action.disabled ? 0.6 : 1,
                      cursor: action.disabled ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: `${action.color}.main`, mr: 2 }}>
                          {action.icon}
                        </Box>
                        <Typography variant="h6">
                          {action.title}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {action.description}
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        component={action.disabled ? 'button' : Link}
                        to={action.disabled ? undefined : action.link}
                        color={action.color}
                        disabled={action.disabled}
                        size="small"
                      >
                        {action.disabled ? 'Coming Soon' : 'Open'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Recent Activity */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Activity
            </Typography>
            
            <List disablePadding>
              {recentActivity.map((activity, index) => (
                <React.Fragment key={index}>
                  <ListItem disablePadding>
                    <ListItemButton>
                      <ListItemIcon>
                        <Notifications />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.title}
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" component="span" color="text.secondary" display="block">
                              {activity.description}
                            </Typography>
                            <Typography variant="caption" component="span" color="text.secondary">
                              {activity.time}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < recentActivity.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard; 